// SettingsIntegrations.jsx
// Self-contained "Connected Accounts" block for the Settings > Integrations tab.
// Drop-in: <SettingsIntegrations />  (no props required)
//
// Dependencies (already in the BookAMeeting folder):
//   - googleCalendar.js  -> connectGoogleCalendar, disconnectGoogleCalendar
//   - meetingService.js  -> getMeetingSettings, updateMeetingSettings
//   - OneOnOneSession.jsx -> teacher session settings component
//   - bookAMeeting.css   -> bm-integ-* styles
//
// Fetches meeting settings and manages Connect / Disconnect + teacher
// "Enable Meetings" toggle + OneOnOneSession settings.

import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useAuth } from "../../features/auth/AuthContext"; // <-- adjust path
import {
  connectGoogleCalendar,
  disconnectGoogleCalendar,
} from "./googleCalendar";
import {
  getMeetingSettings,
  updateMeetingSettings,
} from "./meetingService";
import { google_calendar } from "../../assets/images/images";
import "./bookAMeeting.css";


const SettingsIntegrations = () => {
  const { token } = useAuth();
  const globalState = useSelector((state) => state.globalState);
  const role = globalState?.role;
  const isTeacher = role === "Teacher";

  const [connected, setConnected] = useState(false);
  const [account, setAccount] = useState("");

  const [savingAvail, setSavingAvail] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [error, setError] = useState("");

  const [isMeetingEnabled, setIsMeetingEnabled] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Fetch meeting settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await getMeetingSettings(token);
        if (res.ok && res.data) {
          setIsMeetingEnabled(res.data.is_meeting_enabled ?? false);

          const googleConnected = res.data.google_connected ?? false;
          const googleAccount = res.data.google_account;

          if (googleConnected) {
            setConnected(true);
            setAccount(
              googleAccount?.name ||
                googleAccount?.email ||
                "Connected"
            );
          } else {
            setConnected(false);
          }
        } else {
          console.error("Failed to fetch settings:", res.error);
        }
      } catch (err) {
        console.error("Error fetching settings:", err);
      } finally {
        setSettingsLoaded(true);
      }
    };

    if (isTeacher && token) {
      fetchSettings();
    }
  }, [isTeacher, token]);

  // Auto-dismiss error after 3 seconds
  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => setError(""), 3000);
    return () => clearTimeout(timer);
  }, [error]);

  // Update meeting settings via service
  const updateSettings = async (meetingEnabled) => {
    setSavingSettings(true);
    setError("");

    const res = await updateMeetingSettings(token, {
      is_meeting_enabled: meetingEnabled,
    });

    if (res.ok) {
      setIsMeetingEnabled(meetingEnabled);
    } else {
      setError(res.error || "Failed to save settings");
    }
    setSavingSettings(false);
  };

  const toggleAvailability = async () => {
    if (savingSettings) return;
    const next = !isMeetingEnabled;
    updateSettings(next);
  };

  const handleConnect = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await connectGoogleCalendar(token);
      if (res.ok) {
        const accountName =
          res.data?.data?.google_account?.name ||
          res.data?.data?.google_account?.email ||
          "Connected";
        setConnected(true);
        setAccount(accountName);

        // Auto-enable meetings if currently disabled (for teachers only)
        if (isTeacher && !isMeetingEnabled) {
          try {
            const updateRes = await updateMeetingSettings(token, {
              is_meeting_enabled: true,
            });
            if (updateRes.ok) {
              setIsMeetingEnabled(true);
            }
          } catch (err) {
            console.error("Failed to auto-enable meetings:", err);
          }
        }
      } else {
        setError(res.error || "Could not connect Google Calendar.");
      }
    } catch (e) {
      // Only show error for actual failures, not for popup closure
      const isPopupClosed = /popup|closed|denied|access_denied/i.test(e?.message || "");
      if (!isPopupClosed) {
        setError(e?.message || "Could not connect Google Calendar.");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    const res = await disconnectGoogleCalendar(token);
    setBusy(false);
    if (res.ok) {
      setConnected(false);
      setAccount("");
    } else {
      setError(res.error || "Could not disconnect.");
    }
  };

  return (
    <div className="bm-settings-integrations mt-4">
      <h3 className="bm-settings-heading">Connected Accounts</h3>

      <div className="bm-integrations">
        {/* Both: Google Calendar connect / disconnect */}
        <div className="bm-integ-row">

          
          {/* Teacher only: availability toggle */}
          {isTeacher && (
            <div className="bm-integ-toggle-row mb-4 mt-0">
              <span className="bm-integ-label">Enable Meetings</span>
              <button
                type="button"
                className={`bm-switch ${isMeetingEnabled ? "bm-switch-on" : ""}`}
                onClick={toggleAvailability}
                disabled={savingAvail || savingSettings}
                role="switch"
                aria-checked={isMeetingEnabled}
                aria-label="Enable meetings"
              >
                <span className="bm-switch-knob" />
              </button>
            </div>
          )}

        {error && <p className="bm-error">{error}</p>}

          
        <div className="bm-integ-card">
          <div className="bm-integ-main">
            <span className="bm-integ-logo">
              <img src={google_calendar} alt="google_calendar" />
            </span>
            <div className="bm-integ-info">
              <p className="bm-integ-title">Google Calendar</p>
              <p className="bm-integ-status">
                <span
                  className={`bm-integ-dot ${
                    connected ? "bm-dot-on" : "bm-dot-off"
                  }`}
                />
                {connected
                    ? account
                      ? `Connected as ${account}`
                      : "Connected"
                    : "Not Connected"}
              </p>
            </div>
          </div>
          <span className="bm-integ-action">
            {connected ? (
              <button
                type="button"
                className="bm-outline-btn"
                onClick={handleDisconnect}
                disabled={busy}
              >
                {busy ? "Disconnecting…" : "Disconnect"}
              </button>
            ) : (
              <button
                type="button"
                className="bm-outline-btn"
                onClick={handleConnect}
                disabled={busy}
              >
                {busy ? "Connecting…" : "Connect"}
              </button>
            )}
          </span>
        </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsIntegrations;